import { NavLink } from "@/components/NavLink";
import { Music, Guitar, MessageSquare, ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";

interface MenuCardProps {
  to: string;
  icon: React.ReactNode;
  title: string;
  description: string;
  badge?: string;
  subItems?: { to: string; label: string; icon: React.ReactNode }[];
  onClick: () => void;
}

const MenuCard = ({ to, icon, title, description, badge, subItems, onClick }: MenuCardProps) => {
  return (
    <div className="group rounded-xl bg-gradient-to-br from-card to-muted/20 hover:from-primary/5 hover:to-accent/5 border border-border/50 transition-all duration-300 hover:scale-[1.02] hover:shadow-xl">
      <NavLink
        to={to}
        onClick={onClick}
        className="block p-6"
      >
        <div className="flex items-start gap-4">
          <div className="p-3 rounded-lg bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-all duration-300">
            {icon}
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <h3 className="font-semibold text-lg text-foreground group-hover:text-primary transition-colors">
                {title}
              </h3>
              {badge && (
                <span className="px-2 py-0.5 text-xs font-medium bg-accent/20 text-accent rounded-full">
                  {badge}
                </span>
              )}
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {description}
            </p>
          </div>
          <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all duration-300" />
        </div>
      </NavLink>
      
      {subItems && subItems.length > 0 && (
        <div className="px-6 pb-5 pt-2 space-y-2 border-t border-border/30 mt-2">
          {subItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={onClick}
              className="flex items-center gap-3 p-3 ml-10 rounded-lg hover:bg-muted/50 transition-all duration-200 group/sub"
            >
              <div className="text-muted-foreground group-hover/sub:text-accent transition-colors">
                {item.icon}
              </div>
              <span className="text-sm text-foreground group-hover/sub:text-primary transition-colors">
                {item.label}
              </span>
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
};

interface AusbildungenMegaMenuProps {
  onClose: () => void;
}

export const AusbildungenMegaMenu = ({ onClose }: AusbildungenMegaMenuProps) => {
  return (
    <div className="w-[800px] p-6">
      <div className="mb-4">
        <h2 className="text-2xl font-bold text-foreground mb-1">Ausbildungen</h2>
        <p className="text-sm text-muted-foreground">
          Professionelle Weiterbildungen für pädagogische Fachkräfte
        </p>
      </div>

      <div className="grid gap-4">
        <MenuCard
          to="/ausbildung/fachkraft-musik"
          icon={<Music className="w-6 h-6" />}
          title="Fachkraft für Musik im Elementarbereich"
          description="Umfassende Ausbildung in musikalischer Früherziehung und elementarer Musikpädagogik"
          onClick={onClose}
        />

        <MenuCard
          to="/ausbildung/fachkraft-musik-und-sprache"
          icon={<MessageSquare className="w-6 h-6" />}
          title="Fachkraft für Musik und Sprache im Elementarbereich"
          description="Sprachbildung & Sprachförderung mit Musik – mit Zertifikat"
          onClick={onClose}
        />

        <MenuCard
          to="/ausbildung/fachkraft-gitarre"
          icon={<Guitar className="w-6 h-6" />}
          title="Der große Kita-Gitarrenkurs"
          description="Liedbegleitung in Kita & Krippe – mit Zertifikat"
          onClick={onClose}
        />
      </div>
    </div>
  );
};
